import { useEffect, useState } from 'react'
import { NavLink, Outlet, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api, type Dashboard } from '../lib/api'
import { useAuth } from '../lib/auth'
import ChangePasswordModal from './ChangePasswordModal'

type BadgeKey = 'alerts' | 'enrolments'

interface NavItem {
  to: string
  label: string
  end?: boolean
  badge?: BadgeKey
  minimum?: 'Owner'
}

const NAV: { section: string; items: NavItem[] }[] = [
  {
    section: 'Operations',
    items: [
      { to: '/', label: 'Dashboard', end: true },
      { to: '/messages', label: 'Messages' },
      { to: '/inbound', label: 'Inbound' },
      { to: '/alerts', label: 'Alerts', badge: 'alerts' },
    ],
  },
  {
    section: 'Fleet',
    items: [
      { to: '/devices', label: 'Devices', badge: 'enrolments' },
      { to: '/groups', label: 'Device groups' },
    ],
  },
  {
    section: 'Configuration',
    items: [
      { to: '/sources', label: 'Sources' },
      { to: '/auto-replies', label: 'Auto-replies' },
      { to: '/settings', label: 'Settings' },
    ],
  },
  {
    section: 'Administration',
    items: [
      { to: '/users', label: 'Users', minimum: 'Owner' },
      { to: '/audit', label: 'Audit' },
      { to: '/reports', label: 'Reports' },
    ],
  },
]

export default function Shell() {
  const { user, logout, can } = useAuth()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [changingPassword, setChangingPassword] = useState(false)

  // While the drawer is over the page, the page behind it must not scroll — otherwise a swipe
  // meant for the nav scrolls the content underneath and the drawer appears frozen.
  useEffect(() => {
    if (!open) return
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    window.addEventListener('keydown', onKey)

    return () => {
      document.body.style.overflow = previous
      window.removeEventListener('keydown', onKey)
    }
  }, [open])

  // Badge counts live on the dashboard payload, refreshed on a timer so the sidebar reflects
  // reality without every page polling separately.
  const { data } = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => api.get<Dashboard>('/api/admin/dashboard'),
    refetchInterval: 10_000,
  })

  const badges = {
    alerts: data?.counts.openAlerts ?? 0,
    enrolments: data?.counts.pendingEnrolments ?? 0,
  }

  return (
    <div className="shell">
      {open && (
        <button
          type="button"
          className="sidebar-scrim"
          aria-label="Close menu"
          onClick={() => setOpen(false)}
        />
      )}

      <aside id="sidebar-nav" className={open ? 'sidebar open' : 'sidebar'}>
        <div className="brand">
          <div className="brand-mark">SG</div>
          <div>
            <div className="brand-name">SMS Gateway</div>
            <div className="faint" style={{ fontSize: 10.5, letterSpacing: '0.06em' }}>
              MAURITIUS
            </div>
          </div>
        </div>

        <nav>
          {NAV.map((group) => {
            const items = group.items.filter((i) => !i.minimum || can(i.minimum))
            if (items.length === 0) return null
            return (
              <div key={group.section}>
                <div className="nav-section">{group.section}</div>
                {items.map((item) => {
                  const count = item.badge ? badges[item.badge] : 0
                  return (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      end={item.end ?? false}
                      className={({ isActive }) => (isActive ? 'nav-item active' : 'nav-item')}
                      onClick={() => setOpen(false)}
                    >
                      {item.label}
                      {count > 0 && (
                        <span className={item.badge === 'alerts' ? 'nav-badge alert' : 'nav-badge'}>{count}</span>
                      )}
                    </NavLink>
                  )
                })}
              </div>
            )
          })}
        </nav>

        <div className="sidebar-foot">
          <div className="small" style={{ padding: '0 10px 8px' }}>
            <div style={{ fontWeight: 600 }}>{user?.displayName}</div>
            <div className="faint" style={{ fontSize: 11.5 }}>
              {user?.role}
            </div>
          </div>
          <button className="nav-item" style={{ width: '100%', textAlign: 'left', border: 0, background: 'none', font: 'inherit', cursor: 'pointer' }} onClick={() => setChangingPassword(true)}>
            Change password
          </button>
          <button
            className="nav-item"
            style={{ width: '100%', textAlign: 'left', border: 0, background: 'none', font: 'inherit', cursor: 'pointer' }}
            onClick={async () => {
              await logout()
              navigate('/')
            }}
          >
            Sign out
          </button>
        </div>
      </aside>

      <div className="main">
        <header className="topbar">
          {/*
            The class is what hides this above 860px. It previously carried an inline
            `display: none`, which sits at the top of the cascade and cannot be overridden by a
            media query — so the drawer was unopenable and the console had no navigation at all
            on a phone.
          */}
          <button
            className="btn ghost sm menu-btn"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? 'Close menu' : 'Open menu'}
            aria-expanded={open}
            aria-controls="sidebar-nav"
          >
            ☰
          </button>
          <FleetPulse dashboard={data} />
          <div className="spacer" />
          <span className="faint small">Indian/Mauritius</span>
        </header>

        <main className="content">
          {user?.mustChangePassword && (
            <div className="banner warn">
              <div>
                <strong>Choose a new password.</strong> This account is using a temporary password issued by an
                administrator.{' '}
                <button className="btn sm" style={{ marginLeft: 8 }} onClick={() => setChangingPassword(true)}>
                  Change it now
                </button>
              </div>
            </div>
          )}
          <Outlet />
        </main>
      </div>

      {changingPassword && <ChangePasswordModal onClose={() => setChangingPassword(false)} />}
    </div>
  )
}

/** A one-line fleet summary that is visible from every page. */
function FleetPulse({ dashboard }: { dashboard?: Dashboard }) {
  if (!dashboard) return <span className="faint small">Loading…</span>
  const { online, dark, total, queueDepth, parked } = dashboard.counts

  return (
    <div className="row" style={{ gap: 16 }}>
      <span className="row small" style={{ gap: 7 }}>
        <i className="dot pulse" style={{ color: online > 0 ? 'rgb(var(--ok))' : 'rgb(var(--danger))' }} />
        <span>
          <strong className="num">{online}</strong>
          <span className="faint">/{total}</span> online
        </span>
      </span>
      {dark > 0 && (
        <span className="small" style={{ color: 'rgb(var(--danger))' }}>
          <strong className="num">{dark}</strong> dark
        </span>
      )}
      <span className="small faint">
        queue <strong className="num" style={{ color: 'rgb(var(--ink))' }}>{queueDepth}</strong>
        {parked > 0 && <span style={{ color: 'rgb(var(--warn))' }}> · {parked} parked</span>}
      </span>
    </div>
  )
}
